'use client';

import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { useToast } from '@/app/components/ui/use-toast';
import { useStore } from '@/app/providers/store';
import type { FormImagePrompt } from '@/app/types';
import { getImageSize } from '@/app/utils';
import { resizeAndUploadImage } from '@/app/utils/client';
import { getMergedAndResizedImgHeight, getOriginalAndScaledImgs, mergeImage } from './utils';

const TEMPORARY_UPLOAD_URL_PREFIX = 'ssupload';

export const useImageMerge = (maxImageSize: number) => {
  const { toast } = useToast();
  const trans = useTranslations();
  const { formParams, updateTemplateFormParams } = useStore((state) => ({
    formParams: state.sceneFormParams,
    updateTemplateFormParams: state.updateTemplateFormParams,
  }));
  const { imagePrompts, isMergeImage } = formParams;

  /**
   * 合并图片并resize
   * @param file0 store.imagePrompts存入的img0值
   * @param file1 store.imagePrompts存入的img1值
   * @param originalImgs 未经过缩放的两张图
   * @param scaledImgs 经过缩放后两张图的宽高
   */
  const handleMergeAndResizeImg = (
    file0: FormImagePrompt,
    file1: FormImagePrompt,
    originalImgElements: { img1: HTMLImageElement; img2: HTMLImageElement },
    scaledImgSizes: { img1: { width: number; height: number }; img2: { width: number; height: number } },
  ) => {
    const url = mergeImage(originalImgElements, scaledImgSizes);
    return resizeAndUploadImage({ url, maxImageSize })
      .then((resizedImage) => {
        if (resizedImage?.error?.reason === 'AuditSubmitIllegal') {
          toast({
            type: 'error',
            message: trans('create.createForm.upload.error.AuditSubmitIllegal'),
          });
          return;
        }
        if (resizedImage && !resizedImage.error) {
          if (file0.prompt?.mergeImage) {
            URL.revokeObjectURL(file0.prompt.mergeImage);
          }
          if (file1.prompt?.mergeImage) {
            URL.revokeObjectURL(file1.prompt.mergeImage);
          }
          updateTemplateFormParams('imagePrompts', [
            { ...file0, prompt: { ...file0.prompt, mergeImage: resizedImage.selectedImage } } as FormImagePrompt,
            { ...file1, prompt: { ...file1.prompt, mergeImage: resizedImage.selectedImage } } as FormImagePrompt,
          ]);
          return;
        }
        throw new Error('resizeAndUploadImageError');
      })
      .catch(() => {
        toast({
          type: 'warning',
          message: trans('loadImageError'),
        });
      });
  };

  const triggerMergeOnUpload = async (file0: FormImagePrompt, file1: FormImagePrompt) => {
    if (!file0?.prompt || !file1?.prompt) return;

    try {
      const dimensions = await getOriginalAndScaledImgs(
        file0.prompt.sourceImagePreview,
        file1.prompt.sourceImagePreview,
      );
      const { originalImgElements, scaledImgSizes } = dimensions;
      return handleMergeAndResizeImg(file0, file1, originalImgElements, scaledImgSizes);
    } catch {
      toast({
        type: 'warning',
        message: trans('loadImageError'),
      });
    }
  };

  // 兼容做同款时的历史数据
  useEffect(() => {
    const checkIsNeedMerge = async () => {
      const shouldSkipMerge =
        !isMergeImage ||
        !imagePrompts[0] ||
        !imagePrompts[1] ||
        // 过滤掉第一次merge后修改imagePrompts后再次进入useEffect的情况
        imagePrompts[0]?.prompt?.mergeImage?.includes(TEMPORARY_UPLOAD_URL_PREFIX) ||
        imagePrompts[1]?.prompt?.mergeImage?.includes(TEMPORARY_UPLOAD_URL_PREFIX);

      if (shouldSkipMerge) return;

      try {
        // 获取imagePrompts.mergeImage图的宽高，注意此值经过merge and resize
        const { height: sysMergedAndResizedImgHeight } = await getImageSize(
          imagePrompts[0]?.prompt?.mergeImage || imagePrompts[1]?.prompt?.mergeImage || '',
        );
        const dimensions = await getOriginalAndScaledImgs(
          imagePrompts[0]?.prompt?.sourceImagePreview ?? '',
          imagePrompts[1]?.prompt?.sourceImagePreview ?? '',
        );
        // 获取图片1、2merge后再resized后的高度
        const { originalImgElements, scaledImgSizes } = dimensions;
        const calculateMergeAndResizedImgHeight = getMergedAndResizedImgHeight(scaledImgSizes, maxImageSize);
        // 如果图片merge后的高度和接口中存入的merge的图片高度不相等，则代表是历史数据，需要按新的merge逻辑重新merge，如果相等则代表是新数据，不用再merge
        if (calculateMergeAndResizedImgHeight !== sysMergedAndResizedImgHeight) {
          handleMergeAndResizeImg(
            imagePrompts[0] as FormImagePrompt,
            imagePrompts[1] as FormImagePrompt,
            originalImgElements,
            scaledImgSizes,
          );
        }
      } catch {}
    };
    checkIsNeedMerge();
  }, [imagePrompts]);

  return { triggerMergeOnUpload };
};
